/**
 * Upload Notifications Hook
 * Enhanced hook with retry logic and failure notifications
 * Sprint 6 - Phase 1 Enhancement
 */

import { useState, useCallback, useEffect } from "react";
import {
  showUploadFailureNotification,
  showUploadSuccessNotification,
  showRetryInProgressNotification,
  showNetworkOfflineNotification,
  showNetworkOnlineNotification,
} from '../components/UploadNotification';
import { getRetryManager } from '../services/retryManager';
import { deleteChunks, deleteUpload } from '../idb/chunk-db';
import logger from '../utils/logger';
import { logTelemetry } from '../services/telemetry';

interface UploadNotification {
  videoId: string;
  videoGuid: string;
  timestamp: number;
}

export interface UseUploadNotificationsOptions {
  onRetryUpload?: (videoGuid: string, chunkNo?: number) => void;
  onDiscardUpload?: (videoGuid: string) => void;
}

export const useUploadNotifications = (options: UseUploadNotificationsOptions = {}) => {
  const [completedUploads, setCompletedUploads] = useState<
    Set<UploadNotification>
  >(new Set());
  const retryManager = getRetryManager();

  // Legacy: Handle upload complete
  const handleUploadComplete = useCallback(
    ({ videoId, videoGuid }: { videoId: string; videoGuid: string }) => {
      const timestamp = Date.now();
      setCompletedUploads((prev) => new Set(prev).add({ videoId, videoGuid, timestamp }));
    },
    []
  );

  // Legacy: Handle notification close
  const handleNotificationClose = useCallback((videoId: string) => {
    setCompletedUploads((prev) => {
      const newSet = new Set(prev);
      let itemToDelete: UploadNotification | null = null;

      newSet.forEach((item) => {
        if (item.videoId === videoId) {
          itemToDelete = item;
        }
      });

      if (itemToDelete) {
        newSet.delete(itemToDelete);
      }

      return newSet;
    });
  }, []);

  // Legacy: Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setCompletedUploads(new Set());
  }, []);

  // Legacy: Get latest notification
  const getLatestNotification = useCallback(() => {
    if (completedUploads.size === 0) return null;
    
    let latest: UploadNotification | null = null;
    completedUploads.forEach((notification) => {
      if (!latest || notification.timestamp > latest.timestamp) {
        latest = notification;
      }
    });
    
    return latest;
  }, [completedUploads]);

  // NEW: Handle retry action
  const handleRetry = useCallback(
    async (videoGuid: string, chunkNo?: number) => {
      logger.info(`User initiated retry for ${videoGuid}`, { chunkNo });

      logTelemetry('upload_retry_manual', {
        videoGuid,
        chunkNo,
      });

      if (options.onRetryUpload) {
        options.onRetryUpload(videoGuid, chunkNo);
      }
    },
    [options]
  );

  // NEW: Handle discard action
  const handleDiscard = useCallback(
    async (videoGuid: string) => {
      logger.info(`User discarded upload ${videoGuid}`);

      logTelemetry('upload_discarded', {
        videoGuid,
        retryStats: retryManager.getRetryStats(videoGuid),
      });

      try {
        // Clean up retry states
        retryManager.clearVideoRetryStates(videoGuid);

        // Delete chunks and upload record from IndexedDB
        await deleteChunks(videoGuid);
        await deleteUpload(videoGuid);

        logger.info(`Successfully discarded upload ${videoGuid}`);
      } catch (error) {
        logger.error(`Error discarding upload ${videoGuid}:`, error);
      }

      if (options.onDiscardUpload) {
        options.onDiscardUpload(videoGuid);
      }
    },
    [options, retryManager]
  );

  // NEW: Show upload failure notification
  const notifyUploadFailure = useCallback(
    (videoGuid: string, chunkNo?: number, error?: string) => {
      // Record retry attempt
      retryManager.recordAttempt(videoGuid, chunkNo, error);

      // Show notification
      showUploadFailureNotification({
        videoGuid,
        chunkNo,
        error,
        onRetry: handleRetry,
        onDiscard: handleDiscard,
      });

      logTelemetry('upload_failure_notified', {
        videoGuid,
        chunkNo,
        error,
        attempts: retryManager.getRetryState(videoGuid, chunkNo)?.attempts,
      });
    },
    [retryManager, handleRetry, handleDiscard]
  );

  // NEW: Show upload success notification
  const notifyUploadSuccess = useCallback((videoGuid: string, isBlankMode: boolean = false) => {
    showUploadSuccessNotification(videoGuid, isBlankMode);

    // Clear retry states on success
    retryManager.clearVideoRetryStates(videoGuid);

    logTelemetry('upload_success_notified', {
      videoGuid,
      isBlankMode,
    });
  }, [retryManager]);

  // NEW: Show retry in progress notification
  const notifyRetryInProgress = useCallback(
    (videoGuid: string, totalChunks: number, retryingChunks: number) => {
      showRetryInProgressNotification(videoGuid, totalChunks, retryingChunks);

      logTelemetry('retry_in_progress_notified', {
        videoGuid,
        totalChunks,
        retryingChunks,
      });
    },
    []
  );

  // NEW: Network status notifications
  const notifyNetworkOffline = useCallback(() => {
    showNetworkOfflineNotification();
    logTelemetry('network_offline_notified', {});
  }, []);

  const notifyNetworkOnline = useCallback(() => {
    showNetworkOnlineNotification();
    logTelemetry('network_online_notified', {});
  }, []);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => notifyNetworkOnline();
    const handleOffline = () => notifyNetworkOffline();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [notifyNetworkOnline, notifyNetworkOffline]);

  return {
    // Legacy API (backward compatible)
    completedUploads,
    handleUploadComplete,
    handleNotificationClose,
    clearAllNotifications,
    getLatestNotification,
    
    // New Sprint 6 API
    notifyUploadFailure,
    notifyUploadSuccess,
    notifyRetryInProgress,
    notifyNetworkOffline,
    notifyNetworkOnline,
    handleRetry,
    handleDiscard,
    getRetryState: (videoGuid: string, chunkNo?: number) =>
      retryManager.getRetryState(videoGuid, chunkNo),
    shouldRetry: (videoGuid: string, chunkNo?: number) =>
      retryManager.shouldRetry(videoGuid, chunkNo),
    getRetryStats: (videoGuid: string) => retryManager.getRetryStats(videoGuid),
  };
};
